/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('immersive-gallery')) return;

  var SPACING = 3.6;
  var POOL_SIZE = 16;
  var RECYCLE_BEHIND = 2;
  var RECYCLE_HYSTERESIS = 1;
  var MAX_REHANGS_PER_TICK = 2;
  var TRAVEL_PER_100VH = 11;
  var REFILL_AT = 12;
  var PICK_COLLECTIONS = 5;
  var FALLBACK_PICK = 2;
  var FALLBACK_COUNT = 12;
  var TEX_WIDTH = 800;
  var EXCLUDED_COLLECTIONS = { 'new-releases-archive': 1, frontpage: 1 };
  var EXCLUDED_PRODUCT = 'custom-canvas';

  var WALK_KEY = 'airx:corridor:walk';
  var WALK_VERSION = 1;
  var WALK_MAX_AGE_MS = 30 * 60 * 1000;
  var WALK_MAX_BYTES = 100000;
  var WALK_QUEUE_CAP = 80;

  var LANE_BASE = 2.5;
  var LANE_STEP = 0.3;
  var ART_CLEARANCE = 1.6;
  var LEAN_Y = 0.5;
  var TILT_Y = 0.09;
  var TILT_X = 0.05;

  var DEPTH_RATIO = 0.04;
  var EDGE_STRIP = 0.06;
  var EDGE_DARKEN = 'rgb(209, 209, 209)';
  var EDGE_LONG = 256;
  var EDGE_SHORT = 32;
  var BACK_COLOR = 0xEDE6D8;

  var FLOOR_Y = -2.0;
  var FLOOR_WIDTH = 11;
  var RUNNER_WIDTH = 0.9;
  var FADE_SPAN = 70;
  var FOG_NEAR = 6;
  var PILASTER_X = 4.6;
  var PILASTER_EVERY = 2;
  var RAIL_Y = 2.3;
  var STRIP_Y = 3.05;
  var FURNITURE_MIN_SLOT = 3;
  var GLOW_OPACITY = 0.2;
  var PAGE_WHITE = 0xFAF9F6;
  var GLOW_RGB = '226, 192, 138';
  var MOTE_RGB = '255, 236, 202';

  var DAY_TONES = {
    wood: '231, 222, 208',
    runner: '143, 155, 128',
    pilaster: 0xE3DDD2,
    rail: 0xD8D2C6,
    strip: 0xF6EDDC,
    bench: 0x2A2B27,
    pot: 0xC2B49B,
    leaf: 0x5F6B54,
    leafTip: 0x91B474,
    shadowRgb: '24, 25, 23',
    shadowOpacity: 0.18,
  };
  var NIGHT_TONES = {
    wood: '44, 42, 37',
    runner: '95, 107, 84',
    pilaster: 0x34352F,
    rail: 0x3E3F38,
    strip: 0x5C5340,
    bench: 0x3C3D37,
    pot: 0x54493B,
    leaf: 0x4C563F,
    leafTip: 0x6E8A57,
    shadowRgb: '0, 0, 0',
    shadowOpacity: 0.3,
  };

  function shuffle(list) {
    for (var i = list.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = list[i];
      list[i] = list[j];
      list[j] = tmp;
    }
    return list;
  }

  function fileNameOf(src) {
    return (String(src || '').split('?')[0].split('/').pop()) || '';
  }

  function sceneTheme() {
    var bg = PAGE_WHITE;
    try {
      var raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-bg').trim();
      var m = raw.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
      if (m) {
        var hex = m[1];
        if (hex.length === 3) {
          hex = hex.charAt(0) + hex.charAt(0) +
                hex.charAt(1) + hex.charAt(1) +
                hex.charAt(2) + hex.charAt(2);
        }
        bg = parseInt(hex, 16);
      }
    } catch (e) {  }
    var lum = 0.2126 * ((bg >> 16) & 255) +
              0.7152 * ((bg >> 8) & 255) +
              0.0722 * (bg & 255);
    var dark = lum < 128;
    return { bg: bg, dark: dark, tones: dark ? NIGHT_TONES : DAY_TONES };
  }

  function srgbTex(THREE, tex) {
    if (THREE.SRGBColorSpace && 'colorSpace' in tex) tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  function sizedSrc(src, px) {
    if (!src) return src;
    if (/[?&]width=/.test(src)) return src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + px;
  }

  function isReturnNavigation() {
    try {
      if (performance.getEntriesByType) {
        var nav = performance.getEntriesByType('navigation');
        if (nav && nav.length) {
          return nav[0].type === 'back_forward' || nav[0].type === 'reload';
        }
      }
      if (performance.navigation) {
        var t = performance.navigation.type;
        return t === 2 || t === 1;
      }
    } catch (e) {  }
    return false;
  }

  function packRecord(rec) {
    return {
      id: rec.id,
      t: rec.title,
      u: rec.url,
      p: rec.price,
      m: rec.rawImage || rec.image,
      a: rec.aspect || 0,
    };
  }

  function unpackRecord(row) {
    if (!row || typeof row !== 'object') return null;
    if (typeof row.u !== 'string' || row.u.charAt(0) !== '/') return null;
    if (row.u.charAt(1) === '/' || row.u.charAt(1) === '\\') return null;
    if (typeof row.m !== 'string' || !row.m) return null;
    return {
      id: row.id,
      title: typeof row.t === 'string' ? row.t : '',
      url: row.u,
      price: typeof row.p === 'string' ? row.p : '',
      image: sizedSrc(row.m, TEX_WIDTH),
      rawImage: row.m,
      aspect: Number.isFinite(row.a) ? row.a : 0,
    };
  }

  function unpackList(list, holes) {
    if (!Array.isArray(list) || list.length > 512) return null;
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var row = list[i];
      if (row == null && holes) {
        out.push(null);
        continue;
      }
      var rec = unpackRecord(row);
      if (!rec) return null;
      out.push(rec);
    }
    return out;
  }

  function resolveArt(product) {
    var imgs = product.images || [];
    var first = imgs[0];
    if (!first || !first.src) return null;
    var map = window.DYW_ART_MAP;
    var mini = window.AIRX_MINI_ART;
    var m = fileNameOf(first.src).match(/^(P\d+)/);
    if (m && mini && mini[m[1]]) {
      return { src: mini[m[1]], width: 0, height: 0 };
    }
    if (m && map && map.base && map.art && map.art[m[1]]) {
      return { src: map.base + map.art[m[1]], width: 0, height: 0 };
    }
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i];
      if (im && im.src && fileNameOf(im.src).indexOf('Leaning') !== -1) {
        return { src: im.src, width: im.width || 0, height: im.height || 0 };
      }
    }
    return { src: first.src, width: first.width || 0, height: first.height || 0 };
  }

  function drawEdgeStrip(img, side) {
    var iw = img && (img.naturalWidth || img.width);
    var ih = img && (img.naturalHeight || img.height);
    if (!iw || !ih) return null;
    var vertical = side === 'left' || side === 'right';
    var canvas = document.createElement('canvas');
    canvas.width = vertical ? EDGE_SHORT : EDGE_LONG;
    canvas.height = vertical ? EDGE_LONG : EDGE_SHORT;
    var ctx = canvas.getContext('2d');
    if (!ctx) return null;
    try {
      var strip;
      if (vertical) {
        strip = Math.max(2, Math.round(iw * EDGE_STRIP));
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        if (side === 'right') {
          ctx.drawImage(img, iw - strip, 0, strip, ih, 0, 0, canvas.width, canvas.height);
        } else {
          ctx.drawImage(img, 0, 0, strip, ih, 0, 0, canvas.width, canvas.height);
        }
      } else {
        strip = Math.max(2, Math.round(ih * EDGE_STRIP));
        ctx.translate(0, canvas.height);
        ctx.scale(1, -1);
        if (side === 'top') {
          ctx.drawImage(img, 0, 0, iw, strip, 0, 0, canvas.width, canvas.height);
        } else {
          ctx.drawImage(img, 0, ih - strip, iw, strip, 0, 0, canvas.width, canvas.height);
        }
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = EDGE_DARKEN;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } catch (e) {
      return null;
    }
    return canvas;
  }

  function decorCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }

  function drawFloorCanvas(tones, tailFrac) {
    var c = decorCanvas(64, 128);
    var ctx = c.getContext('2d');
    if (!ctx) return c;
    ctx.fillStyle = 'rgb(' + tones.wood + ')';
    ctx.fillRect(0, 0, 64, 128);
    ctx.strokeStyle = 'rgba(31, 32, 30, 0.05)';
    ctx.lineWidth = 1;
    for (var x = 8; x < 64; x += 12) {
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, 128);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = 'destination-out';
    var soft = ctx.createLinearGradient(0, 0, 64, 0);
    soft.addColorStop(0, 'rgba(0,0,0,1)');
    soft.addColorStop(0.15, 'rgba(0,0,0,0)');
    soft.addColorStop(0.85, 'rgba(0,0,0,0)');
    soft.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = soft;
    ctx.fillRect(0, 0, 64, 128);
    var tail = Math.max(1, Math.round(128 * tailFrac));
    var fade = ctx.createLinearGradient(0, tail, 0, 0);
    fade.addColorStop(0, 'rgba(0,0,0,0)');
    fade.addColorStop(1, 'rgba(0,0,0,0.95)');
    ctx.fillStyle = fade;
    ctx.fillRect(0, 0, 64, tail);
    return c;
  }

  function drawRunnerCanvas(tones, tailFrac) {
    var c = decorCanvas(32, 128);
    var ctx = c.getContext('2d');
    if (!ctx) return c;
    var across = ctx.createLinearGradient(0, 0, 32, 0);
    across.addColorStop(0, 'rgba(' + tones.runner + ', 0)');
    across.addColorStop(0.2, 'rgba(' + tones.runner + ', 0.32)');
    across.addColorStop(0.5, 'rgba(' + tones.runner + ', 0.4)');
    across.addColorStop(0.8, 'rgba(' + tones.runner + ', 0.32)');
    across.addColorStop(1, 'rgba(' + tones.runner + ', 0)');
    ctx.fillStyle = across;
    ctx.fillRect(0, 0, 32, 128);
    ctx.globalCompositeOperation = 'destination-out';
    var tail = Math.max(1, Math.round(128 * tailFrac));
    var fade = ctx.createLinearGradient(0, tail, 0, 0);
    fade.addColorStop(0, 'rgba(0,0,0,0)');
    fade.addColorStop(1, 'rgba(0,0,0,0.95)');
    ctx.fillStyle = fade;
    ctx.fillRect(0, 0, 32, tail);
    return c;
  }

  function drawRadialCanvas(size, rgb, stops) {
    var c = decorCanvas(size, size);
    var ctx = c.getContext('2d');
    if (!ctx) return c;
    var g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    for (var i = 0; i < stops.length; i++) {
      g.addColorStop(stops[i][0], 'rgba(' + rgb + ', ' + stops[i][1] + ')');
    }
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    return c;
  }

  function DecorBuf() {
    this.pos = [];
    this.col = [];
  }
  DecorBuf.prototype.quad = function (a, b, c, d, ca, cb, cc, cd) {
    var P = this.pos;
    var C = this.col;
    P.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    P.push(a[0], a[1], a[2], c[0], c[1], c[2], d[0], d[1], d[2]);
    C.push(ca[0], ca[1], ca[2], cb[0], cb[1], cb[2], cc[0], cc[1], cc[2]);
    C.push(ca[0], ca[1], ca[2], cc[0], cc[1], cc[2], cd[0], cd[1], cd[2]);
  };
  DecorBuf.prototype.box = function (x0, y0, z0, x1, y1, z1, colorFn) {
    var v = [
      [x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0],
      [x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1],
    ];
    var k = [];
    for (var i = 0; i < 8; i++) k.push(colorFn(v[i][0], v[i][1], v[i][2]));
    this.quad(v[0], v[1], v[2], v[3], k[0], k[1], k[2], k[3]);
    this.quad(v[4], v[5], v[6], v[7], k[4], k[5], k[6], k[7]);
    this.quad(v[0], v[4], v[7], v[3], k[0], k[4], k[7], k[3]);
    this.quad(v[1], v[5], v[6], v[2], k[1], k[5], k[6], k[2]);
    this.quad(v[3], v[2], v[6], v[7], k[3], k[2], k[6], k[7]);
    this.quad(v[0], v[1], v[5], v[4], k[0], k[1], k[5], k[4]);
  };
  DecorBuf.prototype.build = function (THREE) {
    var geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    return geo;
  };

  class ImmersiveGallery extends HTMLElement {
    connectedCallback() {
      this.fallback = this.querySelector('[data-corridor-fallback]');
      this.scrollWrap = this.querySelector('[data-corridor-scroll]');
      this.stage = this.querySelector('[data-corridor-stage]');
      this.hint = this.querySelector('[data-corridor-hint]');
      this.caption = this.querySelector('[data-corridor-caption]');

      if (this.caption && this.caption.tagName === 'A') this.caption.target = '_blank';
      this.captionTitle = this.querySelector('[data-corridor-caption-title]');
      this.captionPrice = this.querySelector('[data-corridor-caption-price]');
      this.skipBtn = this.querySelector('[data-corridor-skip]');

      this.seeds = this.readJSON('[data-corridor-products]') || [];
      var strings = this.readJSON('[data-corridor-strings]') || {};
      this.fromLabel = strings.from || 'From';
      this.curated = this.dataset.corridorCurated === 'true';

      this.queue = [];
      this.seenPool = [];
      this.slotArtwork = [];
      this.usedIds = Object.create(null);
      this.collectionPool = [];
      this.refilling = false;
      this.queueSettled = false;
      this.meshes = [];
      this.initialized = false;
      this.disposed = false;
      this.walkSavePending = false;
      this.resume = null;
      this.tookScrollRestoration = false;
      this.prevScrollRestoration = '';
      this.restoreRaf = null;

      this.onContextMenu = function (e) { e.preventDefault(); };
      this.addEventListener('contextmenu', this.onContextMenu);
      this.setImagesUnsaveable();

      if (!this.canRun()) {

        this.randomizeFallback();
        return;
      }

      this.resume = this.readWalk();
      if (this.resume) {
        this.takeScrollRestoration();
        this.initialized = true;
        this.init();
        return;
      }

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting && !self.initialized) {
            self.initialized = true;
            self.observer.disconnect();
            self.init();
          }
        },
        { rootMargin: '150% 0px 150% 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.teardown();
    }

    readJSON(selector) {
      var node = this.querySelector(selector);
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (e) { return null; }
    }

    setImagesUnsaveable() {
      var imgs = this.querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) imgs[i].draggable = false;
    }

    canRun() {
      if (!this.stage || !this.scrollWrap) return false;
      if (!window.gsap || !window.ScrollTrigger) return false;
      if (!document.documentElement.classList.contains('motion')) return false;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

      var coarse = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
      var memory = navigator.deviceMemory || 8;
      var cores = navigator.hardwareConcurrency || 8;
      if (coarse && (memory <= 4 || cores <= 4)) return false;

      try {
        var probe = document.createElement('canvas');
        var gl = probe.getContext('webgl2') || probe.getContext('webgl');
        if (!gl) return false;
      } catch (e) {
        return false;
      }
      return true;
    }

    fetchJSON(url) {
      var opts = { headers: { Accept: 'application/json' } };
      if (this.aborter) opts.signal = this.aborter.signal;
      return fetch(url, opts).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      });
    }

    bootQueue(pickCount) {
      var self = this;
      this.aborter = typeof AbortController === 'function' ? new AbortController() : null;

      var seedRecords = [];
      this.seeds.forEach(function (s) {
        if (!s || !s.image || self.usedIds[s.id]) return;
        self.usedIds[s.id] = 1;
        var raw = s.image;
        var map = window.DYW_ART_MAP;
        var m = fileNameOf(raw).match(/^(P\d+)/);
        if (m && map && map.base && map.art && map.art[m[1]]) raw = map.base + map.art[m[1]];
        else if (s.leaning) raw = s.leaning;
        seedRecords.push({
          id: s.id,
          title: s.title || '',
          url: s.url,
          price: s.price || '',
          image: sizedSrc(raw, TEX_WIDTH),
          rawImage: raw,
          aspect: parseFloat(s.aspect) || 0,
        });
      });

      if (!this.curated) shuffle(seedRecords);
      this.queue = seedRecords;

      this.queueReady = this.discover(pickCount).then(function () {
        self.queueSettled = true;
      });
      return this.queueReady;
    }

    discover(pickCount) {
      var self = this;
      return this.fetchJSON('/collections.json?limit=250')
        .then(function (data) {
          var handles = [];
          ((data && data.collections) || []).forEach(function (c) {
            if (!c || !c.handle) return;
            if (EXCLUDED_COLLECTIONS[c.handle]) return;

            if (typeof c.products_count === 'number' && c.products_count <= 0) return;
            handles.push(c.handle);
          });
          shuffle(handles);
          var picked = handles.splice(0, pickCount);
          self.collectionPool = handles;
          if (!picked.length) return;
          return Promise.all(
            picked.map(function (h) {
              return self.fetchCollectionProducts(h).catch(function () { return []; });
            })
          ).then(function (lists) {
            var merged = [];
            lists.forEach(function (l) { merged.push.apply(merged, l); });
            self.appendProducts(shuffle(merged), !self.curated);
          });
        })
        .catch(function () {

        });
    }

    fetchCollectionProducts(handle) {
      return this.fetchJSON(
        '/collections/' + encodeURIComponent(handle) + '/products.json?limit=250'
      ).then(function (data) {
        var out = [];
        ((data && data.products) || []).forEach(function (p) {
          if (!p || p.handle === EXCLUDED_PRODUCT) return;
          if (!p.images || !p.images.length || !p.images[0] || !p.images[0].src) return;
          out.push(p);
        });
        return out;
      });
    }

    appendProducts(products, reshuffleQueue) {
      var self = this;
      var added = false;
      products.forEach(function (p) {
        if (self.usedIds[p.id]) return;
        var rec = self.toRecord(p);
        if (!rec) return;
        self.usedIds[p.id] = 1;
        self.queue.push(rec);
        added = true;
      });
      if (added && reshuffleQueue) shuffle(this.queue);
      return added;
    }

    toRecord(p) {
      var art = resolveArt(p);
      if (!art) return null;
      return {
        id: p.id,
        title: p.title || '',
        url: '/products/' + p.handle,
        price: this.priceText(p),
        image: sizedSrc(art.src, TEX_WIDTH),
        rawImage: art.src,
        aspect: art.width > 0 && art.height > 0 ? art.width / art.height : 0,
      };
    }

    priceText(p) {
      var min = Infinity;
      var max = -Infinity;
      (p.variants || []).forEach(function (v) {
        var n = parseFloat(v && v.price);
        if (isFinite(n)) {
          if (n < min) min = n;
          if (n > max) max = n;
        }
      });
      if (!isFinite(min)) return '';
      var cents = Math.round(min * 100);
      var money;
      try {
        money = window.AirX && window.AirX.formatMoney
          ? window.AirX.formatMoney(cents)
          : '$' + (cents / 100).toFixed(2);
      } catch (e) {
        money = '$' + (cents / 100).toFixed(2);
      }
      return max > min ? this.fromLabel + ' ' + money : money;
    }

    artworkForSlot(slot) {
      if (this.slotArtwork[slot]) return this.slotArtwork[slot];
      var rec = this.nextArtwork();
      if (rec) this.slotArtwork[slot] = rec;
      return rec;
    }

    nextArtwork() {
      if (!this.queue.length && this.seenPool.length &&
          this.queueSettled && !this.refilling &&
          (!this.collectionPool || !this.collectionPool.length)) {

        this.queue = shuffle(this.seenPool.splice(0));
      }
      if (!this.queue.length) {
        this.refill();
        return null;
      }
      var rec = this.queue.shift();
      this.seenPool.push(rec);
      if (this.queue.length < REFILL_AT) this.refill();
      return rec;
    }

    refill() {
      var self = this;
      if (this.refilling || this.disposed) return;
      if (!this.collectionPool || !this.collectionPool.length) return;
      this.refilling = true;
      var handle = this.collectionPool.shift();
      this.fetchCollectionProducts(handle)
        .then(function (products) {
          if (self.disposed) return;
          self.appendProducts(shuffle(products), false);
        })
        .catch(function () {  })
        .then(function () {
          self.refilling = false;
          if (!self.disposed && self.queue.length < REFILL_AT &&
              self.collectionPool && self.collectionPool.length) {
            self.refill();
          }
        });
    }

    readWalk() {
      var runVh = parseInt(this.dataset.corridorRun, 10) || 600;
      try {
        var raw = window.sessionStorage && sessionStorage.getItem(WALK_KEY);
        if (!raw || raw.length > WALK_MAX_BYTES) return null;
        if (!isReturnNavigation()) {

          sessionStorage.removeItem(WALK_KEY);
          return null;
        }
        var data = JSON.parse(raw);
        if (!data || data.v !== WALK_VERSION || data.pendingReturn !== true) return null;
        if (typeof data.t !== 'number' || Date.now() - data.t > WALK_MAX_AGE_MS) return null;

        if (!Number.isFinite(data.progress) || data.progress < 0 || data.progress > 1) return null;

        if (data.runVh !== runVh) return null;
        if (data.depth !== (runVh / 100) * TRAVEL_PER_100VH) return null;
        var slots = unpackList(data.slots, true);
        var queue = unpackList(data.queue, false);
        if (!slots || !queue) return null;
        var art = queue.length;
        for (var i = 0; i < slots.length; i++) {
          if (slots[i]) art += 1;
        }
        if (art < 3) return null;
        var collections = [];
        if (Array.isArray(data.collections)) {
          data.collections.forEach(function (h) {
            if (typeof h === 'string' && h) collections.push(h);
          });
        }
        return { progress: data.progress, slots: slots, queue: queue, collections: collections };
      } catch (e) {
        return null;
      }
    }

    bootWalkFromSave(saved) {
      var self = this;
      this.aborter = typeof AbortController === 'function' ? new AbortController() : null;
      this.slotArtwork = saved.slots.slice();
      this.queue = saved.queue.slice();
      this.collectionPool = saved.collections.slice();
      saved.slots.forEach(function (rec) {
        if (!rec) return;
        if (rec.id != null) self.usedIds[rec.id] = 1;
        self.seenPool.push(rec);
      });
      saved.queue.forEach(function (rec) {
        if (rec.id != null) self.usedIds[rec.id] = 1;
      });
      this.queueSettled = true;
      this.queueReady = Promise.resolve();
      return this.queueReady;
    }

    saveWalk() {
      if (!this.scrollTrigger || this.disposed) return;
      var slots = [];
      for (var i = 0; i < this.slotArtwork.length; i++) {
        slots.push(this.slotArtwork[i] ? packRecord(this.slotArtwork[i]) : null);
      }
      var runVh = parseInt(this.dataset.corridorRun, 10) || 600;
      var data = {
        v: WALK_VERSION,
        t: Date.now(),
        pendingReturn: true,
        progress: this.scrollTrigger.progress,
        runVh: runVh,
        depth: this.depth,
        slots: slots,
        queue: this.queue.slice(0, WALK_QUEUE_CAP).map(packRecord),
        collections: (this.collectionPool || []).slice(0, 40),

        clickUrl: this.walkClickUrl || null,
      };
      try {
        var json = JSON.stringify(data);
        if (json.length > WALK_MAX_BYTES) {
          data.queue = [];
          json = JSON.stringify(data);
        }
        if (json.length > WALK_MAX_BYTES) return;
        sessionStorage.setItem(WALK_KEY, json);
      } catch (e) {

      }
    }

    clearPendingReturn() {
      this.walkSavePending = false;
      try { sessionStorage.removeItem(WALK_KEY); } catch (e) {  }
    }

    restoreWalk() {
      var self = this;
      var saved = this.resume;
      this.restoreRaf = requestAnimationFrame(function () {
        self.restoreRaf = requestAnimationFrame(function () {
          self.restoreRaf = null;
          if (self.disposed || !self.scrollTrigger) return;
          window.ScrollTrigger.refresh();
          var st = self.scrollTrigger;
          var top = st.start + saved.progress * (st.end - st.start);
          try {
            window.scrollTo({ top: top, left: 0, behavior: 'instant' });
          } catch (e) {

            var root = document.documentElement;
            var prev = root.style.scrollBehavior;
            root.style.scrollBehavior = 'auto';
            window.scrollTo(0, top);
            root.style.scrollBehavior = prev;
          }
          self.progress = saved.progress;
          self.camera.position.z = 4 - saved.progress * self.depth;
          self.resume = null;
          self.clearPendingReturn();
          self.releaseScrollRestoration();
        });
      });
    }

    takeScrollRestoration() {
      try {
        if ('scrollRestoration' in window.history) {
          this.prevScrollRestoration = window.history.scrollRestoration;
          window.history.scrollRestoration = 'manual';
          this.tookScrollRestoration = true;
        }
      } catch (e) {  }
    }

    releaseScrollRestoration() {
      if (!this.tookScrollRestoration) return;
      this.tookScrollRestoration = false;
      try {
        window.history.scrollRestoration = this.prevScrollRestoration || 'auto';
      } catch (e) {  }
    }

    init() {
      var self = this;

      if (this.resume) this.bootWalkFromSave(this.resume);
      else this.bootQueue(PICK_COLLECTIONS);
      import(this.dataset.threeSrc)
        .then(function (mod) {
          var THREE = mod.default || mod;
          if (self.disposed || !THREE || !THREE.WebGLRenderer) {
            self.releaseScrollRestoration();
            return;
          }

          if (self.resume || self.queue.length >= 3) {
            self.build(THREE);
            return;
          }
          return self.queueReady.then(function () {
            if (!self.disposed && self.queue.length >= 3) self.build(THREE);
          });
        })
        .catch(function () {

          self.releaseScrollRestoration();
        });
    }

    build(THREE) {
      var self = this;
      var gsap = window.gsap;
      this.THREE = THREE;

      this.fallback.classList.add('visually-hidden-focusable');
      this.scrollWrap.hidden = false;
      this.scrollWrap.setAttribute('aria-hidden', 'true');

      var renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'low-power',
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      if ('outputColorSpace' in renderer && THREE.SRGBColorSpace) {
        renderer.outputColorSpace = THREE.SRGBColorSpace;
      }
      this.renderer = renderer;
      this.stage.appendChild(renderer.domElement);
      this.stage.classList.add('is-interactive');

      var scene = new THREE.Scene();
      this.scene = scene;

      var camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
      camera.position.set(0, 0, 4);
      this.camera = camera;

      var runVh = parseInt(this.dataset.corridorRun, 10) || 600;
      this.depth = (runVh / 100) * TRAVEL_PER_100VH;

      this.progress = this.resume ? this.resume.progress : 0;
      camera.position.z = 4 - this.progress * this.depth;
      var baseSlot = Math.max(
        0,
        Math.ceil((-camera.position.z - RECYCLE_BEHIND) / SPACING)
      );

      this.buildDecor(THREE);

      this.loader = new THREE.TextureLoader();
      this.loader.crossOrigin = 'anonymous';
      this.geometry = new THREE.BoxGeometry(1, 1, 1);
      this.meshes = [];
      this.lastFill = 0;
      this.lastFirstSlot = baseSlot;

      for (var i = 0; i < POOL_SIZE; i++) {
        var mesh = new THREE.Mesh(this.geometry, this.makeCanvasMaterials(THREE));
        mesh.userData = { product: null, ready: false, loadId: 0, baseRotY: 0, slot: 0, lane: LANE_BASE };
        this.attachArtDecor(mesh);
        this.placeAtSlot(mesh, baseSlot + i);
        scene.add(mesh);
        this.meshes.push(mesh);
        this.assignArtwork(mesh, this.artworkForSlot(baseSlot + i));
      }

      this.scrollTrigger = window.ScrollTrigger.create({
        trigger: this.scrollWrap,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.6,
        onUpdate: function (st) {
          if (self.resume) return;
          self.progress = st.progress;
          if (self.hint) self.hint.classList.toggle('is-hidden', st.progress > 0.04);
        },
        onToggle: function (st) {

          if (self.skipBtn) self.skipBtn.classList.toggle('is-visible', st.isActive);
        },
      });

      this.onSkip = function () {
        var top;
        if (self.scrollTrigger) {

          top = self.scrollTrigger.end + window.innerHeight;
        } else {
          var rect = self.scrollWrap.getBoundingClientRect();
          top = window.scrollY + rect.bottom;
        }
        try {
          window.scrollTo({ top: top, behavior: 'smooth' });
        } catch (e) {
          window.scrollTo(0, top);
        }
      };
      if (this.skipBtn) this.skipBtn.addEventListener('click', this.onSkip);

      this.pointer = { x: 0, y: 0 };
      this.raycaster = new THREE.Raycaster();
      this.ndc = new THREE.Vector2();
      this.hovered = null;

      this.onPointerMove = function (e) {
        var rect = self.stage.getBoundingClientRect();
        self.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        self.pointer.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
        self.pointerPx = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      };
      this.onClick = function () {
        if (self.hovered && self.hovered.userData.product) {

          self.walkSavePending = true;
          self.walkClickUrl = self.hovered.userData.product.url;
          self.saveWalk();
          var tab = window.open(self.hovered.userData.product.url, '_blank');
          if (!tab) window.location.href = self.hovered.userData.product.url;
        }
      };
      this.onLeave = function () {
        self.pointer.x = 0;
        self.pointer.y = 0;
        self.setHovered(null);
      };
      this.stage.addEventListener('pointermove', this.onPointerMove);
      this.stage.addEventListener('click', this.onClick);
      this.stage.addEventListener('pointerleave', this.onLeave);

      this.onPageHide = function () {
        if (self.walkSavePending) self.saveWalk();
      };
      this.onPageShow = function (e) {
        if (e.persisted) self.clearPendingReturn();
      };
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);

      this.onResize = function () {
        var w = self.stage.clientWidth;
        var h = self.stage.clientHeight;
        if (!w || !h) return;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      };
      this.onResize();
      window.addEventListener('resize', this.onResize);

      this.tick = this.tick.bind(this);
      this.running = false;
      this.runObserver = new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        if (visible && !self.running) {
          self.running = true;
          gsap.ticker.add(self.tick);
        } else if (!visible && self.running) {
          self.running = false;
          gsap.ticker.remove(self.tick);
        }
      });
      this.runObserver.observe(this.scrollWrap);

      window.ScrollTrigger.refresh();

      if (this.resume) this.restoreWalk();
    }

    placeAtSlot(mesh, slot) {
      var side = slot % 2 === 0 ? -1 : 1;
      var lane = LANE_BASE + (slot % 3) * LANE_STEP;
      mesh.userData.slot = slot;
      mesh.userData.lane = lane;
      mesh.position.x = side * lane;
      mesh.position.y = ((slot % 3) - 1) * 0.35;
      mesh.position.z = -slot * SPACING;
      mesh.userData.baseRotY = side * -LEAN_Y;
      mesh.rotation.y = mesh.userData.baseRotY;
      mesh.rotation.x = 0;

      var shadow = mesh.userData.shadow;
      if (shadow) shadow.position.set(mesh.position.x, FLOOR_Y + 0.02, mesh.position.z);
      var glow = mesh.userData.glow;
      if (glow) {
        glow.position.set(
          mesh.position.x + side * 0.35,
          mesh.position.y + 0.75,
          mesh.position.z - 0.3
        );
      }
    }

    buildDecor(THREE) {
      var scene = this.scene;
      var totalSlots = Math.ceil(this.depth / SPACING) + POOL_SIZE + 2;
      var farZ = totalSlots * SPACING;
      var length = farZ + 12;
      var tailFrac = Math.min(0.3, 16 / length);
      var theme = sceneTheme();
      var tones = theme.tones;
      this.decorTheme = theme;
      this.decorParams = { totalSlots: totalSlots, farZ: farZ, tailFrac: tailFrac };
      this.decorGeos = [];
      this.decorMats = [];
      var self = this;
      var srgb = function (tex) { return srgbTex(THREE, tex); };
      var track = function (geo, mat) {
        if (geo) self.decorGeos.push(geo);
        if (mat) self.decorMats.push(mat);
      };

      scene.fog = new THREE.Fog(theme.bg, FOG_NEAR, FOG_NEAR + FADE_SPAN);

      var floorTex = srgb(new THREE.CanvasTexture(drawFloorCanvas(tones, tailFrac)));
      var floorGeo = new THREE.PlaneGeometry(FLOOR_WIDTH, length);
      var floorMat = new THREE.MeshBasicMaterial({ map: floorTex, transparent: true });
      var floor = new THREE.Mesh(floorGeo, floorMat);
      floor.rotation.x = -Math.PI / 2;
      floor.position.set(0, FLOOR_Y, 8 - length / 2);
      floor.renderOrder = -2;
      scene.add(floor);
      track(floorGeo, floorMat);
      this.floorMat = floorMat;

      var runnerTex = srgb(new THREE.CanvasTexture(drawRunnerCanvas(tones, tailFrac)));
      var runnerGeo = new THREE.PlaneGeometry(RUNNER_WIDTH, length);
      var runnerMat = new THREE.MeshBasicMaterial({
        map: runnerTex,
        transparent: true,
        depthWrite: false,
      });
      var runner = new THREE.Mesh(runnerGeo, runnerMat);
      runner.rotation.x = -Math.PI / 2;
      runner.position.set(0, FLOOR_Y + 0.012, 8 - length / 2);
      runner.renderOrder = -1;
      scene.add(runner);
      track(runnerGeo, runnerMat);
      this.runnerMat = runnerMat;

      var archGeo = this.buildArchGeometry(THREE, tones, totalSlots, farZ);
      var archMat = new THREE.MeshBasicMaterial({
        vertexColors: true,
        side: THREE.DoubleSide,
      });
      this.archMesh = new THREE.Mesh(archGeo, archMat);
      scene.add(this.archMesh);
      track(archGeo, archMat);

      var moteTex = srgb(new THREE.CanvasTexture(
        drawRadialCanvas(32, MOTE_RGB, [[0, 0.9], [0.5, 0.4], [1, 0]])
      ));
      var moteCount = Math.max(50, Math.min(70, Math.round(farZ / 2.2)));
      var small = Math.round(moteCount * 0.6);
      var layers = [
        { count: small, size: 0.025 },
        { count: moteCount - small, size: 0.045 },
      ];
      this.motes = [];
      for (var L = 0; L < layers.length; L++) {
        var n = layers[L].count;
        var base = new Float32Array(n * 3);
        var phase = new Float32Array(n);
        for (var p = 0; p < n; p++) {
          var mx = (Math.random() * 2 - 1) * 4;
          var my = -1.6 + Math.random() * 3.8;

          if (Math.abs(mx) < 0.9 && Math.abs(my) < 0.6) {
            mx = (mx < 0 ? -1 : 1) * (0.9 + Math.random() * 3.1);
          }
          base[p * 3] = mx;
          base[p * 3 + 1] = my;
          base[p * 3 + 2] = 4 - Math.random() * farZ;
          phase[p] = Math.random() * Math.PI * 2;
        }
        var moteGeo = new THREE.BufferGeometry();
        var attr = new THREE.Float32BufferAttribute(new Float32Array(base), 3);
        if (attr.setUsage && THREE.DynamicDrawUsage) attr.setUsage(THREE.DynamicDrawUsage);
        moteGeo.setAttribute('position', attr);
        var moteMat = new THREE.PointsMaterial({
          map: moteTex,
          size: layers[L].size,
          transparent: true,
          opacity: 0.25,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          sizeAttenuation: true,
        });
        var points = new THREE.Points(moteGeo, moteMat);
        points.renderOrder = 5;
        points.frustumCulled = false;
        scene.add(points);
        this.motes.push({ count: n, base: base, phase: phase, attr: attr });
        track(moteGeo, moteMat);
      }

      var shadowTex = srgb(new THREE.CanvasTexture(
        drawRadialCanvas(64, tones.shadowRgb, [[0, 0.85], [0.45, 0.5], [1, 0]])
      ));
      this.shadowGeo = new THREE.PlaneGeometry(1, 1);
      this.shadowMat = new THREE.MeshBasicMaterial({
        map: shadowTex,
        transparent: true,
        opacity: tones.shadowOpacity,
        depthWrite: false,
      });
      track(this.shadowGeo, this.shadowMat);

      var glowTex = srgb(new THREE.CanvasTexture(
        drawRadialCanvas(128, GLOW_RGB, [[0, 0.85], [0.4, 0.32], [1, 0]])
      ));
      this.glowMat = new THREE.SpriteMaterial({
        map: glowTex,
        transparent: true,
        opacity: GLOW_OPACITY,
        depthWrite: false,
      });
      track(null, this.glowMat);

      this.colorModeObserver = new MutationObserver(function () {
        self.onColorModeChange();
      });
      this.colorModeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-color-mode'],
      });
    }

    buildArchGeometry(THREE, tones, totalSlots, farZ) {
      var tone = function (hex) {
        var c = new THREE.Color(hex);
        var rgb = [c.r, c.g, c.b];
        return function () { return rgb; };
      };
      var buf = new DecorBuf();
      var slot;
      var side;

      var pilasterFn = tone(tones.pilaster);
      for (slot = 0; slot <= totalSlots; slot += PILASTER_EVERY) {
        var pz = -slot * SPACING;
        for (side = -1; side <= 1; side += 2) {
          buf.box(side * PILASTER_X - 0.09, FLOOR_Y, pz - 0.09,
                  side * PILASTER_X + 0.09, RAIL_Y + 0.55, pz + 0.09, pilasterFn);
        }
      }

      var railFn = tone(tones.rail);
      for (side = -1; side <= 1; side += 2) {
        var rx = side * (PILASTER_X - 0.14);
        for (var zs = 6; zs > -farZ; zs -= SPACING * 4) {
          buf.box(rx - 0.03, RAIL_Y, Math.max(zs - SPACING * 4, -farZ),
                  rx + 0.03, RAIL_Y + 0.06, zs, railFn);
        }
      }

      var stripFn = tone(tones.strip);
      for (slot = 5; slot <= totalSlots; slot += PILASTER_EVERY) {
        var sz = -slot * SPACING;
        var sc = stripFn(0, STRIP_Y, sz);
        buf.quad(
          [-1.6, STRIP_Y, sz - 0.1], [1.6, STRIP_Y, sz - 0.1],
          [1.6, STRIP_Y, sz + 0.1], [-1.6, STRIP_Y, sz + 0.1],
          sc, sc, sc, sc);
      }

      var benchFn = tone(tones.bench);
      var potFn = tone(tones.pot);
      var leafLoFn = tone(tones.leaf);
      var leafHiFn = tone(tones.leafTip);
      var kind = 0;
      for (slot = FURNITURE_MIN_SLOT; slot < totalSlots; slot += (kind % 2 ? 6 : 5)) {
        var fz = -(slot + 0.5) * SPACING;
        var fs = kind % 2 ? 1 : -1;
        var fx = fs * (1.35 + (kind % 3) * 0.12);
        if (kind % 2 === 0) {

          buf.box(fx - 0.21, FLOOR_Y + 0.32, fz - 0.75,
                  fx + 0.21, FLOOR_Y + 0.42, fz + 0.75, benchFn);
          buf.box(fx - 0.17, FLOOR_Y, fz - 0.62, fx + 0.17, FLOOR_Y + 0.32, fz - 0.5, benchFn);
          buf.box(fx - 0.17, FLOOR_Y, fz + 0.5, fx + 0.17, FLOOR_Y + 0.32, fz + 0.62, benchFn);
        } else {

          buf.box(fx - 0.13, FLOOR_Y, fz - 0.13,
                  fx + 0.13, FLOOR_Y + 0.28, fz + 0.13, potFn);
          var lo = leafLoFn(fx, 0, fz);
          var hi = leafHiFn(fx, 0, fz);
          for (var leaf = 0; leaf < 2; leaf++) {
            var la = 0.6 + leaf * (Math.PI / 2);
            var lx = Math.cos(la);
            var lz = Math.sin(la);
            buf.quad(
              [fx - lx * 0.24, FLOOR_Y + 0.2, fz - lz * 0.24],
              [fx + lx * 0.24, FLOOR_Y + 0.2, fz + lz * 0.24],
              [fx + lx * 0.07, FLOOR_Y + 1.0, fz + lz * 0.07],
              [fx - lx * 0.07, FLOOR_Y + 1.0, fz - lz * 0.07],
              lo, lo, hi, hi);
          }
        }
        kind += 1;
      }
      return buf.build(THREE);
    }

    onColorModeChange() {
      if (this.disposed || !this.scene || !this.THREE || !this.decorParams) return;
      var theme = sceneTheme();
      var old = this.decorTheme;
      if (old && old.bg === theme.bg && old.dark === theme.dark) return;
      this.decorTheme = theme;
      var THREE = this.THREE;
      var tones = theme.tones;
      var params = this.decorParams;
      if (this.scene.fog) this.scene.fog.color.setHex(theme.bg);
      if (this.floorMat) {
        if (this.floorMat.map) this.floorMat.map.dispose();
        this.floorMat.map = srgbTex(THREE,
          new THREE.CanvasTexture(drawFloorCanvas(tones, params.tailFrac)));
        this.floorMat.needsUpdate = true;
      }
      if (this.runnerMat) {
        if (this.runnerMat.map) this.runnerMat.map.dispose();
        this.runnerMat.map = srgbTex(THREE,
          new THREE.CanvasTexture(drawRunnerCanvas(tones, params.tailFrac)));
        this.runnerMat.needsUpdate = true;
      }
      if (this.shadowMat) {
        if (this.shadowMat.map) this.shadowMat.map.dispose();
        this.shadowMat.map = srgbTex(THREE, new THREE.CanvasTexture(
          drawRadialCanvas(64, tones.shadowRgb, [[0, 0.85], [0.45, 0.5], [1, 0]])
        ));
        this.shadowMat.opacity = tones.shadowOpacity;
        this.shadowMat.needsUpdate = true;
      }
      if (this.archMesh) {

        var fresh = this.buildArchGeometry(THREE, tones, params.totalSlots, params.farZ);
        this.archMesh.geometry.dispose();
        this.archMesh.geometry = fresh;
        if (this.decorGeos) this.decorGeos.push(fresh);
      }
    }

    attachArtDecor(mesh) {
      var THREE = this.THREE;
      var shadow = new THREE.Mesh(this.shadowGeo, this.shadowMat);
      shadow.rotation.x = -Math.PI / 2;
      shadow.visible = false;
      this.scene.add(shadow);
      var glow = new THREE.Sprite(this.glowMat);
      glow.visible = false;
      this.scene.add(glow);
      mesh.userData.shadow = shadow;
      mesh.userData.glow = glow;
    }

    dressArtwork(mesh, w, h) {
      var shadow = mesh.userData.shadow;
      var glow = mesh.userData.glow;
      if (shadow) {
        var sw = w * 1.15 + 0.2;
        var sd = w * 0.45 + 0.3;
        shadow.visible = true;
        if (window.gsap) {
          shadow.scale.set(sw * 0.6, sd * 0.6, 1);
          window.gsap.to(shadow.scale, { x: sw, y: sd, duration: 0.9, ease: 'power2.out' });
        } else {
          shadow.scale.set(sw, sd, 1);
        }
      }
      if (glow) {
        glow.scale.set(w * 1.9 + 0.9, h * 1.5 + 0.9, 1);
        glow.visible = true;
      }
    }

    makeCanvasMaterials(THREE) {
      var mats = [];
      for (var f = 0; f < 6; f++) {
        mats.push(new THREE.MeshBasicMaterial({
          color: f === 4 ? 0xffffff : BACK_COLOR,
          transparent: true,
          opacity: 0,
          fog: false,
        }));
      }
      return mats;
    }

    maxWidthForLane(lane, h) {
      var cosMin = Math.cos(LEAN_Y - TILT_Y);
      var sinMax = Math.sin(LEAN_Y + TILT_Y);
      var depth = Math.max(h * DEPTH_RATIO, 0.04);
      var zHalf = (h / 2) * Math.sin(TILT_X) + depth / 2;
      return Math.max(0.6, (2 * (lane - ART_CLEARANCE - zHalf * sinMax)) / cosMin);
    }

    assignArtwork(mesh, record) {
      var self = this;
      var mats = mesh.material;
      var i;
      mesh.userData.product = record || null;
      mesh.userData.ready = false;
      mesh.userData.loadId += 1;
      var loadId = mesh.userData.loadId;
      if (this.hovered === mesh) this.setHovered(null);
      if (window.gsap) window.gsap.killTweensOf(mats);
      for (i = 0; i < mats.length; i++) {
        if (mats[i].map) {
          mats[i].map.dispose();
          mats[i].map = null;
        }
        if (i !== 4) mats[i].color.setHex(BACK_COLOR);
        mats[i].opacity = 0;
        mats[i].needsUpdate = true;
      }

      var shadow = mesh.userData.shadow;
      var glow = mesh.userData.glow;
      if (shadow) {
        if (window.gsap) window.gsap.killTweensOf(shadow.scale);
        shadow.visible = false;
      }
      if (glow) glow.visible = false;
      if (!record) return;

      var THREE = this.THREE;
      var attempt = function (url, isRetry) {
        self.loader.load(
          url,
          function (texture) {
            if (self.disposed || mesh.userData.loadId !== loadId) {
              texture.dispose();
              return;
            }
            if (THREE.SRGBColorSpace && 'colorSpace' in texture) {
              texture.colorSpace = THREE.SRGBColorSpace;
            }
            var img = texture.image;
            var aspect = img && img.width > 0 && img.height > 0
              ? img.width / img.height
              : (record.aspect || 0.8);
            aspect = Math.min(Math.max(aspect, 0.4), 4);
            var h = aspect >= 1 ? 1.7 : 2.1;
            var w = h * aspect;

            var maxW = self.maxWidthForLane(mesh.userData.lane, h);
            if (w > maxW) {
              h *= maxW / w;
              w = maxW;
            }
            mesh.scale.set(w, h, Math.max(h * DEPTH_RATIO, 0.04));
            mats[4].map = texture;
            mats[4].needsUpdate = true;
            self.applyWrapEdges(mesh, img);
            self.dressArtwork(mesh, w, h);
            mesh.userData.ready = true;
            window.gsap.to(mats, { opacity: 1, duration: 0.9, ease: 'power2.out' });
          },
          undefined,
          function () {
            if (self.disposed || mesh.userData.loadId !== loadId) return;

            if (!isRetry && record.rawImage && url !== record.rawImage) {
              attempt(record.rawImage, true);
            }
          }
        );
      };
      attempt(record.image, false);
    }

    applyWrapEdges(mesh, img) {
      var THREE = this.THREE;
      var mats = mesh.material;
      var sides = ['right', 'left', 'top', 'bottom'];
      for (var i = 0; i < sides.length; i++) {
        var canvas = drawEdgeStrip(img, sides[i]);
        if (!canvas) continue;
        var tex = new THREE.CanvasTexture(canvas);
        if (THREE.SRGBColorSpace && 'colorSpace' in tex) {
          tex.colorSpace = THREE.SRGBColorSpace;
        }
        mats[i].map = tex;
        mats[i].color.setHex(0xffffff);
        mats[i].needsUpdate = true;
      }
    }

    fillEmpties() {
      for (var i = 0; i < this.meshes.length && this.queue.length; i++) {
        var mesh = this.meshes[i];
        if (!mesh.userData.product) {
          var rec = this.artworkForSlot(mesh.userData.slot);
          if (!rec) break;
          this.assignArtwork(mesh, rec);
        }
      }
    }

    tick() {
      var camera = this.camera;
      var lerp = function (a, b, t) { return a + (b - a) * t; };

      var targetZ = 4 - this.progress * this.depth;
      camera.position.z = lerp(camera.position.z, targetZ, 0.12);
      camera.position.x = lerp(camera.position.x, this.pointer.x * 0.35, 0.06);
      camera.position.y = lerp(camera.position.y, -this.pointer.y * 0.2, 0.06);

      var i;
      var mesh;
      var firstSlot = Math.max(
        0,
        Math.ceil((-camera.position.z - RECYCLE_BEHIND) / SPACING)
      );
      var lastSlot = firstSlot + POOL_SIZE - 1;
      var occupied = Object.create(null);
      var strays = [];
      for (i = 0; i < this.meshes.length; i++) {
        mesh = this.meshes[i];
        var slot = mesh.userData.slot;
        if (slot >= firstSlot - RECYCLE_HYSTERESIS &&
            slot <= lastSlot + RECYCLE_HYSTERESIS &&
            !occupied[slot]) {
          occupied[slot] = 1;
        } else {
          strays.push(mesh);
        }
      }
      if (strays.length) {

        var down = firstSlot >= this.lastFirstSlot;
        var free = down ? firstSlot : lastSlot;
        var budget = MAX_REHANGS_PER_TICK;
        for (i = 0; i < strays.length && budget > 0; i++) {
          if (down) {
            while (free <= lastSlot && occupied[free]) free += 1;
            if (free > lastSlot) break;
          } else {
            while (free >= firstSlot && occupied[free]) free -= 1;
            if (free < firstSlot) break;
          }
          var rec = this.artworkForSlot(free);
          if (!rec) break;
          occupied[free] = 1;
          this.placeAtSlot(strays[i], free);
          this.assignArtwork(strays[i], rec);
          budget -= 1;
        }
      }
      this.lastFirstSlot = firstSlot;

      var now = Date.now();
      if (now - this.lastFill > 500) {
        this.lastFill = now;
        this.fillEmpties();
      }

      for (i = 0; i < this.meshes.length; i++) {
        mesh = this.meshes[i];
        mesh.rotation.y = lerp(mesh.rotation.y, mesh.userData.baseRotY + this.pointer.x * TILT_Y, 0.08);
        mesh.rotation.x = lerp(mesh.rotation.x, -this.pointer.y * TILT_X, 0.08);
      }

      this.ndc.set(this.pointer.x, -this.pointer.y);
      this.raycaster.setFromCamera(this.ndc, camera);
      var hits = this.raycaster.intersectObjects(this.meshes, false);
      var target = null;
      for (i = 0; i < hits.length; i++) {
        var u = hits[i].object.userData;
        if (u && u.product && u.ready) {
          target = hits[i].object;
          break;
        }
      }
      this.setHovered(target);

      if (this.caption && !this.caption.hidden && this.pointerPx) {
        this.caption.style.transform =
          'translate(' + (this.pointerPx.x + 18) + 'px, ' + (this.pointerPx.y + 18) + 'px)';
      }

      if (this.motes) {
        var mt = performance.now() * 0.00022;
        for (i = 0; i < this.motes.length; i++) {
          var layer = this.motes[i];
          var arr = layer.attr.array;
          for (var p = 0; p < layer.count; p++) {
            var i3 = p * 3;
            var ph = layer.phase[p];
            arr[i3] = layer.base[i3] + Math.sin(mt * 1.3 + ph) * 0.4;
            arr[i3 + 1] = layer.base[i3 + 1] + Math.sin(mt * 0.8 + ph * 1.7) * 0.3;
            arr[i3 + 2] = layer.base[i3 + 2] + Math.cos(mt + ph * 0.9) * 0.35;
          }
          layer.attr.needsUpdate = true;
        }
      }

      this.renderer.render(this.scene, camera);
    }

    setHovered(mesh) {
      if (mesh === this.hovered) return;
      this.hovered = mesh;
      if (!this.caption) return;
      if (mesh && mesh.userData.product) {
        this.captionTitle.textContent = mesh.userData.product.title;
        this.captionPrice.textContent = mesh.userData.product.price;
        this.caption.setAttribute('href', mesh.userData.product.url);
        this.caption.hidden = false;
      } else {
        this.caption.hidden = true;
      }
    }

    randomizeFallback() {
      var self = this;
      if (!this.fallback || typeof fetch !== 'function') return;

      if (this.curated && this.seeds.length) return;
      var grid = this.fallback.querySelector('.product-grid');
      if (!grid) return;
      this.bootQueue(FALLBACK_PICK).then(function () {
        if (self.disposed || self.queue.length < 4) return;
        var picks = self.queue.slice(0, FALLBACK_COUNT);
        self.renderFallbackCards(grid, picks);
      });
    }

    renderFallbackCards(grid, records) {

      var existingMedia = grid.querySelector('.product-card__media');
      var mediaClass = existingMedia
        ? existingMedia.className
        : 'product-card__media artwork-tile product-card__media--portrait';
      var frag = document.createDocumentFragment();
      records.forEach(function (rec) {
        var article = document.createElement('article');
        article.className = 'product-card';

        var media = document.createElement('div');
        media.className = mediaClass;
        var figure = document.createElement('figure');
        figure.className = 'product-card__figure';
        var img = document.createElement('img');
        img.className = 'product-card__img product-card__img--primary';
        img.loading = 'lazy';
        img.draggable = false;
        img.src = rec.image;

        if (rec.rawImage && rec.rawImage !== rec.image) {
          img.onerror = function () {
            img.onerror = null;
            img.src = rec.rawImage;
          };
        }
        img.alt = rec.title;
        figure.appendChild(img);
        media.appendChild(figure);
        article.appendChild(media);

        var info = document.createElement('div');
        info.className = 'product-card__info';
        var h3 = document.createElement('h3');
        h3.className = 'product-card__title';
        var link = document.createElement('a');
        link.className = 'product-card__link';
        link.href = rec.url;
        link.textContent = rec.title;
        h3.appendChild(link);
        info.appendChild(h3);
        if (rec.price) {
          var priceWrap = document.createElement('div');
          priceWrap.className = 'product-card__price';
          var price = document.createElement('span');
          price.className = 'price';
          var regular = document.createElement('span');
          regular.className = 'price__regular';
          regular.textContent = rec.price;
          price.appendChild(regular);
          priceWrap.appendChild(price);
          info.appendChild(priceWrap);
        }
        article.appendChild(info);
        frag.appendChild(article);
      });
      grid.textContent = '';
      grid.appendChild(frag);
    }

    teardown() {
      this.disposed = true;
      if (this.aborter) {
        try { this.aborter.abort(); } catch (e) {}
        this.aborter = null;
      }
      if (this.onContextMenu) {
        this.removeEventListener('contextmenu', this.onContextMenu);
        this.onContextMenu = null;
      }
      if (this.skipBtn) {
        if (this.onSkip) this.skipBtn.removeEventListener('click', this.onSkip);
        this.skipBtn.classList.remove('is-visible');
      }

      if (this.restoreRaf) {
        cancelAnimationFrame(this.restoreRaf);
        this.restoreRaf = null;
      }
      if (this.onPageHide) {
        window.removeEventListener('pagehide', this.onPageHide);
        this.onPageHide = null;
      }
      if (this.onPageShow) {
        window.removeEventListener('pageshow', this.onPageShow);
        this.onPageShow = null;
      }
      this.resume = null;
      this.releaseScrollRestoration();
      if (this.observer) this.observer.disconnect();
      if (this.runObserver) this.runObserver.disconnect();
      if (this.colorModeObserver) {
        this.colorModeObserver.disconnect();
        this.colorModeObserver = null;
      }
      if (this.scrollTrigger) this.scrollTrigger.kill();
      if (this.running && window.gsap) window.gsap.ticker.remove(this.tick);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.stage && this.onPointerMove) {
        this.stage.removeEventListener('pointermove', this.onPointerMove);
        this.stage.removeEventListener('click', this.onClick);
        this.stage.removeEventListener('pointerleave', this.onLeave);
      }
      if (this.meshes) {
        this.meshes.forEach(function (mesh) {
          if (mesh.userData && mesh.userData.shadow && window.gsap) {
            window.gsap.killTweensOf(mesh.userData.shadow.scale);
          }
          if (!mesh.material) return;
          var mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          if (window.gsap) window.gsap.killTweensOf(mats);
          mats.forEach(function (m) {
            if (m.map) m.map.dispose();
            m.dispose();
          });
        });
        this.meshes = [];
      }
      if (this.geometry) this.geometry.dispose();

      if (this.decorMats) {
        this.decorMats.forEach(function (m) {
          if (m.map) m.map.dispose();
          m.dispose();
        });
        this.decorMats = null;
      }
      if (this.decorGeos) {
        this.decorGeos.forEach(function (g) { g.dispose(); });
        this.decorGeos = null;
      }
      this.motes = null;
      this.shadowGeo = null;
      this.shadowMat = null;
      this.glowMat = null;
      this.floorMat = null;
      this.runnerMat = null;
      this.archMesh = null;
      this.decorParams = null;
      this.decorTheme = null;
      if (this.renderer) {
        this.renderer.dispose();
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
        this.renderer = null;
      }

      if (this.fallback) this.fallback.classList.remove('visually-hidden-focusable');
      if (this.scrollWrap) {
        this.scrollWrap.hidden = true;
        this.scrollWrap.removeAttribute('aria-hidden');
      }

      if (this.scrollTrigger && window.ScrollTrigger) window.ScrollTrigger.refresh();
    }
  }

  customElements.define('immersive-gallery', ImmersiveGallery);
})();
